import { useCallback, useEffect, useRef, useState } from "react";
import {
  BOTTOM_MARGIN_PX,
  DROPDOWN_OPENED_EVENT,
  FLIP_THRESHOLD_TO_BOTTOM,
  FLIP_THRESHOLD_TO_TOP,
  MIN_DROPDOWN_HEIGHT,
  TOP_MARGIN_PX,
  VIEWPORT_SIDE_MARGIN_PX,
} from "./selectConstants";

/**
 * Shared mechanics for selector poppers.
 *
 * Selection state deliberately stays in each selector component. The existing
 * selectors use different controlled/semi-controlled models and close rules,
 * so this hook only manages the field, search, placement and focus lifecycle.
 */
export default function useSelectDropdown({
  instanceId,
  showSearch = true,
  forceTop = false,
  onClosed,
} = {}) {
  const anchorRef = useRef(null);
  const paperRef = useRef(null);
  const searchInputRef = useRef(null);
  const popperRef = useRef(null);
  const placementRef = useRef("bottom-start");

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(MIN_DROPDOWN_HEIGHT);
  const [anchorIsFixed, setAnchorIsFixed] = useState(false);
  const [actualPlacement, setActualPlacement] = useState("bottom-start");
  // Measured on open/resize so the panel width never depends on a render-time
  // ref read, which is still empty on the first paint.
  const [anchorWidth, setAnchorWidth] = useState(0);
  // Room from the field's left edge to the viewport's right margin (start alignment).
  const [availableWidth, setAvailableWidth] = useState(0);
  // Room from the viewport's left margin to the field's right edge (end alignment).
  const [availableWidthEnd, setAvailableWidthEnd] = useState(0);

  const close = useCallback(
    ({ clearSearch = true, refocus = false } = {}) => {
      setOpen(false);
      setIsFocused(false);
      if (clearSearch) setSearchTerm("");
      if (refocus) anchorRef.current?.focus();
      onClosed?.();
    },
    [onClosed]
  );

  const computePlacement = useCallback(() => {
    if (forceTop) return "top-start";
    if (!anchorRef.current) return placementRef.current;

    const { bottom } = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    const current = placementRef.current;
    const opensUpward = current.startsWith("top");

    // Different thresholds prevent flickering when the available space is near
    // the boundary and a resize/scroll triggers repeated measurements.
    if (!opensUpward && spaceBelow < FLIP_THRESHOLD_TO_TOP) return "top-start";
    if (opensUpward && spaceBelow > FLIP_THRESHOLD_TO_BOTTOM) return "bottom-start";
    return current;
  }, [forceTop]);

  const recalculate = useCallback(() => {
    if (!anchorRef.current) return;

    const placement = computePlacement();
    placementRef.current = placement;
    setActualPlacement(placement);

    const { top, bottom, left, right, width } = anchorRef.current.getBoundingClientRect();

    const available = placement.startsWith("top")
      ? top - TOP_MARGIN_PX
      : window.innerHeight - bottom - BOTTOM_MARGIN_PX;
    setDropdownMaxHeight(Math.max(available, MIN_DROPDOWN_HEIGHT));

    // Start alignment shares the field's left edge and can only use space to the right.
    // End alignment shares the field's right edge and can only use space to the left.
    // Both stay inside the viewport so the overlay cannot widen the page.
    setAnchorWidth(width);
    setAvailableWidth(Math.max(window.innerWidth - left - VIEWPORT_SIDE_MARGIN_PX, width));
    setAvailableWidthEnd(Math.max(right - VIEWPORT_SIDE_MARGIN_PX, width));
  }, [computePlacement]);

  const detectAnchorIsFixed = useCallback(() => {
    let element = anchorRef.current?.parentElement;
    let scanElement = element;
    let insideDialog = false;

    // DialogContent is often the first scrollable ancestor, while role=dialog
    // exists higher in the tree. Scan once before evaluating overflow.
    while (scanElement && scanElement !== document.body) {
      if (scanElement.getAttribute("role") === "dialog") {
        insideDialog = true;
        break;
      }
      scanElement = scanElement.parentElement;
    }

    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;

      if (/auto|scroll|overlay/.test(overflow)) return insideDialog;
      if (style.position === "sticky" || style.position === "fixed") return true;
      element = element.parentElement;
    }

    return insideDialog;
  }, []);

  useEffect(() => {
    const handleOtherDropdownOpened = (event) => {
      // Accept the historical raw-id payload while CompanyHierarchy is migrated.
      const openedId = event.detail?.id ?? event.detail;
      if (openedId !== instanceId && open) close();
    };

    window.addEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    return () => window.removeEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
  }, [close, instanceId, open]);

  useEffect(() => {
    if (!open) return;

    window.dispatchEvent(
      new CustomEvent(DROPDOWN_OPENED_EVENT, { detail: { id: instanceId } })
    );
  }, [instanceId, open]);

  useEffect(() => {
    if (!open) return;

    setAnchorIsFixed(detectAnchorIsFixed());
    recalculate();

    const focusTarget = showSearch ? searchInputRef : paperRef;
    const focusTimer = window.setTimeout(() => focusTarget.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [detectAnchorIsFixed, open, recalculate, showSearch]);

  useEffect(() => {
    if (!open) return;

    const handleResize = () => recalculate();
    const viewport = window.visualViewport;

    if (viewport) {
      viewport.addEventListener("resize", handleResize);
      return () => viewport.removeEventListener("resize", handleResize);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, recalculate]);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (anchorRef.current) {
        const { top, bottom } = anchorRef.current.getBoundingClientRect();
        const opensUpward = placementRef.current.startsWith("top");

        if ((opensUpward && top > window.innerHeight) || (!opensUpward && bottom < 0)) {
          close();
          return;
        }
      }

      // Absolute poppers follow document scrolling. Fixed poppers inside dialogs
      // need an explicit position refresh as their scroll container moves.
      if (anchorIsFixed) popperRef.current?.forceUpdate();
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [anchorIsFixed, close, open]);

  // Tab and focus leaving the widget must close the dropdown so onClosed
  // (availability refresh) runs the same way as click-away.
  useEffect(() => {
    if (!open) return;

    const isInsideWidget = (target) => {
      const root = anchorRef.current;
      const paper = paperRef.current;
      return Boolean((root && root.contains(target)) || (paper && paper.contains(target)));
    };

    const handleTab = (event) => {
      if (event.key === "Tab") close();
    };

    const handleFocusIn = (event) => {
      if (!isInsideWidget(event.target)) close();
    };

    document.addEventListener("keydown", handleTab, true);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("keydown", handleTab, true);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [close, open]);

  return {
    open,
    setOpen,
    close,
    searchTerm,
    setSearchTerm,
    isFocused,
    setIsFocused,
    actualPlacement,
    dropdownMaxHeight,
    anchorWidth,
    availableWidth,
    availableWidthEnd,
    anchorIsFixed,
    anchorRef,
    paperRef,
    searchInputRef,
    popperRef,
  };
}
